import { useState } from "react";
import { ZoomIn } from "lucide-react";
import { Layout } from "./LegalPage";
import { getLang } from "../i18n";
import { Dialog, DialogContent } from "../components/ui/dialog";

const members = [
  {
    photo: "/images/team/emanuele-petrucci.jpg",
    name: "Emanuele Petrucci",
    role: {
      it: "Supervisione e controllo dei sistemi informatici e software gestionali USTP",
      en: "Supervision and control of USTP IT systems and management software",
    },
    bio: {
      it: "Ingegnere aerospaziale. Ruolo attuale: GNSS Galileo Configuration Manager presso EUSPA (EU Agency For The Space Programme). Si occupa della gestione della configurazione per il sistema di navigazione satellitare europeo Galileo. Settori di competenza: Sistemi informatici aerospaziali satellitari. Ha frequentato, tra le varie istituzioni, il Dublin Institute of Technology. Si alterna fra Terni e Praga, la città in cui risiede e la sede in cui svolge gran parte del suo lavoro nella sede aerospaziale.",
      en: "Aerospace engineer. Current role: GNSS Galileo Configuration Manager at EUSPA (EU Agency For The Space Programme). He handles configuration management for the European Galileo satellite navigation system. Areas of expertise: aerospace satellite IT systems. Among other institutions, he attended the Dublin Institute of Technology. He alternates between Terni and Prague, the city where he lives and the location where he carries out most of his work at the aerospace site.",
    },
  },
  {
    photo: "/images/team/eric-munoz.jpg",
    name: "Eric Munoz",
    role: {
      it: "Co-Founder e Sviluppatore USTP",
      en: "Co-Founder and USTP Developer",
    },
    bio: {
      it: "È un trader e investitore indipendente, Ingegnere informatico, parla 8 lingue, è specializzato in finanza decentralizzata. Gestisce vari progetti DeFi. Sposato con Alexandra Gerbey (Entrepreneur, Art-Director), è anche Direttore di un laboratorio di sovranità tecnologica in Spagna, dove risiede. Possiede il 5% dell'intero asset USTP.",
      en: "He is an independent trader and investor, a computer engineer, speaks 8 languages and specializes in decentralized finance. He manages various DeFi projects. Married to Alexandra Gerbey (Entrepreneur, Art Director), he is also Director of a technological sovereignty lab in Spain, where he lives. He owns 5% of the entire USTP asset.",
    },
  },
  {
    photo: "/images/team/bruno-giordana.jpg",
    name: "Bruno Giordana",
    role: {
      it: "Musicista, arrangiatore",
      en: "Musician, arranger",
    },
    bio: {
      it: "Musicista poliedrico, riconosciuto per la sua eccellenza tecnica e la sua immediatezza nel suo contributo musicale. È un sassofonista, tastierista, arrangiatore e programmatore. Ha collaborazioni di alto profilo nel panorama della canzone italiana: ha affiancato artisti importanti come Renato Zero e Fiorella Mannoia in album e tournées. Ha realizzato la prima versione strumentale cine/pop di Universe ed altri brani, di cui è anche co-autore insieme ad Abacico.",
      en: "A versatile musician, recognised for his technical excellence and the immediacy of his musical contribution. He is a saxophonist, keyboard player, arranger and programmer. He has high-profile collaborations in the Italian song scene: he has worked alongside major artists such as Renato Zero and Fiorella Mannoia on albums and tours. He created the first cine/pop instrumental version of Universe and other tracks, of which he is also co-author together with Abacico.",
    },
  },
  {
    photo: "/images/team/fabrizio-marioni-v2.webp",
    name: "Fabrizio Marioni",
    role: {
      it: "Founder · Compositore, pianista, tastierista",
      en: "Founder · Composer, pianist, keyboardist",
    },
    bio: {
      it: "Classe 1970, nato a Orbassano (TO), risiede in Sardegna dal 2020. Dopo aver maturato formazione ed esperienza nel campo della Blockchain dal 2013, ha ideato e lavora a tempo pieno al progetto USTP, che intende in un prossimo futuro consegnare/affidare ad altre persone ed entità per gli scopi che esso si prefigge.",
      en: "Born in 1970 in Orbassano (TO), he has lived in Sardinia since 2020. Having built training and experience in the field of Blockchain since 2013, he conceived and works full-time on the USTP project, which he intends, in the near future, to hand over/entrust to other people and entities for the goals it sets itself.",
    },
  },
];

const pageTitle = { it: "Il Team", en: "The Team" };

export default function TeamPage() {
  const lang = getLang();
  const [preview, setPreview] = useState(null);
  return (
    <Layout title={pageTitle[lang]} updated="Universe Score Token Protocol">
      <div data-testid="team-content" className="grid gap-6 sm:grid-cols-2">
        {members.map((m, i) => (
          <article
            key={m.name}
            data-testid={`team-card-${i + 1}`}
            className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm"
          >
            {m.photo ? (
              <button
                type="button"
                onClick={() => setPreview(m)}
                data-testid={`team-photo-btn-${i + 1}`}
                aria-label={`${m.name} — anteprima`}
                className="group relative h-20 w-20 overflow-hidden rounded-full ring-1 ring-slate-200 shadow-md transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-sky-500"
              >
                <img
                  src={m.photo}
                  alt={m.name}
                  data-testid={`team-photo-${i + 1}`}
                  className="h-full w-full object-cover"
                />
                <span className="absolute inset-0 flex items-center justify-center bg-slate-900/0 text-white opacity-0 transition-all group-hover:bg-slate-900/40 group-hover:opacity-100">
                  <ZoomIn size={20} />
                </span>
              </button>
            ) : (
              <div
                data-testid={`team-photo-placeholder-${i + 1}`}
                aria-label={m.name}
                className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-sky-500 to-sky-700 font-display text-xl font-bold text-white ring-1 ring-slate-200 shadow-md"
              >
                {m.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
              </div>
            )}
            <h2
              className="mt-5 font-display text-xl font-bold text-slate-900"
              data-testid={`team-name-${i + 1}`}
            >
              {m.name}
            </h2>
            <p
              className="mt-2 font-mono text-[11px] uppercase tracking-[0.15em] text-sky-600"
              data-testid={`team-role-${i + 1}`}
            >
              {m.role[lang]}
            </p>
            <p className="mt-4 text-sm text-slate-600 leading-relaxed" data-testid={`team-bio-${i + 1}`}>
              {m.bio[lang]}
            </p>
          </article>
        ))}
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent
          className="max-w-md rounded-3xl bg-white p-4 border border-slate-200"
          data-testid="team-photo-modal"
        >
          {preview && (
            <figure className="flex flex-col items-center">
              <img
                src={preview.photo}
                alt={preview.name}
                data-testid="team-photo-large"
                className="w-full rounded-2xl object-contain max-h-[70vh]"
              />
              <figcaption className="mt-4 text-center">
                <p className="font-display text-lg font-bold text-slate-900">{preview.name}</p>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.15em] text-sky-600">
                  {preview.role[lang]}
                </p>
              </figcaption>
            </figure>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
